<script setup lang="ts">
import type {
  JobFilterComparison,
  JobFilterLookupOptionType,
  JobFilterType,
  JobLookupFilterValue,
  MultiselectOption,
} from '@ironsight/types'
import { Component, computed, inject, PropType, Ref, shallowRef, toRef, watch } from 'vue'
import VsButton from '@/components/vision/VsButton.vue'
import DateTimeJobFilterField from '@/components/jobs/job-search-page/filter-fields/DateTimeJobFilterField.vue'
import NumberInputJobFilterField from '@/components/jobs/job-search-page/filter-fields/NumberInputJobFilterField.vue'
import BooleanInputJobFilterField from '@/components/jobs/job-search-page/filter-fields/BooleanInputJobFilterField.vue'
import TextInputJobFilterField from '@/components/jobs/job-search-page/filter-fields/TextInputJobFilterField.vue'
import PriorityJobFilterField from '@/components/jobs/job-search-page/filter-fields/PriorityJobFilterField.vue'
import MultiselectJobFilterField from '@/components/jobs/job-search-page/filter-fields/MultiselectJobFilterField.vue'
import { useResourceTypeJobFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useResourceTypeJobFilter'
import { useCostCenterFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useCostCenterFilter'
import { useResourceFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useResourceFilter'
import LocationJobFilter from '@/components/jobs/job-search-page/filter-fields/LocationJobFilter.vue'
import {
  useAllUsersFilter,
  useFieldWorkerFilter,
  useRequesterFilter,
  useSubmitterFilter,
  useUsersFromOrgFilter,
  useUsersWithUuidFromOrgFilter,
} from '@/components/jobs/job-search-page/filter-fields/data-providers/useUserFilters'
import { useTagsFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useTagsFilter'
import { useProjectFilterDataProvider } from '@/components/jobs/job-search-page/filter-fields/data-providers/useProjectFilter'
import { useInventorySubTypeFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useInventorySubTypeFilter'
import { useJobStatusFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useJobStatusFilter'
import { useActivityFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useActivityFilter'
import {
  useBusinessUnitFilter,
  useCompanyFilter,
  useDivisionFilter,
  useHubFilter,
  useOrganizationIdFilter,
  useServiceProviderFilter,
} from '@/components/jobs/job-search-page/filter-fields/data-providers/useBusinessUnitFilter'
import UnknownJobFilterField from '@/components/jobs/job-search-page/filter-fields/UnknownJobFilterField.vue'
import { useBatchFilterDataProvider } from './filter-fields/data-providers/useBatchFilter'
import PaginatedMultiselectJobFilterField from './filter-fields/PaginatedMultiselectJobFilterField.vue'
import { PaginatedDataProvider } from '@/models/types/paginated-data-provider'
import { SUPPORTED_JOB_FILTER_OPERATIONS } from '@/components/jobs/job-search-page/useJobFilterProperties'
import {
  filterContextBridgeKey,
  type FiltersTopBarEntity,
} from '@/components/filters/useFilterContextBridge'
import { useBatchSyncStatusFilterProvider } from '@/components/batches/useBatchSyncStatusFilterProvider'
import { useSelectAllToggle } from '@/components/jobs/job-search-page/useSelectAllToggle'

const BasicComponentRegistry: Record<JobFilterType, Component> = {
  DateTime: DateTimeJobFilterField,
  Integer: NumberInputJobFilterField,
  Double: NumberInputJobFilterField,
  Decimal: NumberInputJobFilterField,
  Boolean: BooleanInputJobFilterField,
  Text: TextInputJobFilterField,
  // These should throw -- cannot be returned from here as they are not basic
  IntLookup: UnknownJobFilterField,
  StringLookup: UnknownJobFilterField,
}
type Provider = { options: Ref<MultiselectOption[]> } | PaginatedDataProvider

const LookupComponentRegistry: Record<
  JobFilterLookupOptionType,
  {
    component: Component
    dataProvider?: (() => { options: Ref<MultiselectOption[]> }) | (() => PaginatedDataProvider)
    componentOptions?: Record<string, unknown>
  }
> = {
  OwnerId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useDivisionFilter(),
  },
  HubId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useHubFilter(),
  },
  ResourceTypeId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useResourceTypeJobFilter(),
  },
  CostCenter: {
    component: MultiselectJobFilterField,
    dataProvider: () => useCostCenterFilter(),
    componentOptions: {
      allowCreateFromSearch: true,
    },
  },
  ResourceId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useResourceFilter(),
  },
  ServiceProviderId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useServiceProviderFilter(),
  },
  LocationId: {
    component: LocationJobFilter,
    componentOptions: { supportsSelectAll: true },
  },
  PickupLocationId: {
    component: LocationJobFilter,
    componentOptions: { supportsSelectAll: true },
  },
  ConfirmedPickupLocationId: {
    component: LocationJobFilter,
    componentOptions: { supportsSelectAll: true },
  },
  DestinationLocationId: {
    component: LocationJobFilter,
    componentOptions: { supportsSelectAll: true },
  },
  ConfirmedDestinationLocationId: {
    component: LocationJobFilter,
    componentOptions: { supportsSelectAll: true },
  },
  Priority: {
    component: PriorityJobFilterField,
  },
  RequesterSid: {
    component: MultiselectJobFilterField,
    dataProvider: () => useRequesterFilter(),
  },
  SubmitterSid: {
    component: MultiselectJobFilterField,
    dataProvider: () => useSubmitterFilter(),
  },
  FieldWorker: {
    component: MultiselectJobFilterField,
    dataProvider: () => useFieldWorkerFilter(),
  },
  Followers: {
    component: MultiselectJobFilterField,
    dataProvider: () => useAllUsersFilter(),
  },
  Tags: {
    component: MultiselectJobFilterField,
    dataProvider: () => useTagsFilter(),
  },
  ProjectId: {
    component: PaginatedMultiselectJobFilterField,
    dataProvider: () => useProjectFilterDataProvider(),
  },
  InventoryType: {
    // Placeholders below
    component: PriorityJobFilterField,
  },
  InventorySubTypeId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useInventorySubTypeFilter(),
  },
  ConfirmedInventorySubTypeId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useInventorySubTypeFilter(),
  },
  ConfirmedDropoffInventorySubTypeId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useInventorySubTypeFilter(),
  },
  Status: {
    component: MultiselectJobFilterField,
    dataProvider: () => useJobStatusFilter(),
  },
  TaskId: {
    component: MultiselectJobFilterField,
    dataProvider: () => useActivityFilter(),
    componentOptions: { supportsSelectAll: true },
  },
  Business_unit: {
    component: MultiselectJobFilterField,
    dataProvider: () => useBusinessUnitFilter(),
  },
  Company: {
    component: MultiselectJobFilterField,
    dataProvider: () => useCompanyFilter(),
  },
  Location: {
    component: LocationJobFilter,
    componentOptions: { supportsSelectAll: true },
  },
  User: {
    component: MultiselectJobFilterField,
    dataProvider: () => useAllUsersFilter(),
  },
  Batch: {
    component: PaginatedMultiselectJobFilterField,
    dataProvider: () => useBatchFilterDataProvider(),
  },
}

type BatchFilterLookupOptionType =
  | 'BatchNumber'
  | 'Creator'
  | 'Customer'
  | 'QboSyncStatus'
  | 'QboSyncedBy'
  | 'Location'
  | 'User'
  | 'Business_unit'
  | 'Company'
const BatchLookupComponentRegistry: Record<
  BatchFilterLookupOptionType,
  {
    component: Component
    dataProvider?: (() => { options: Ref<MultiselectOption[]> }) | (() => PaginatedDataProvider)
  }
> = {
  BatchNumber: {
    component: PaginatedMultiselectJobFilterField,
    dataProvider: () => useBatchFilterDataProvider(),
  },
  Creator: {
    component: MultiselectJobFilterField,
    dataProvider: () => useUsersFromOrgFilter(),
  },
  Customer: {
    component: MultiselectJobFilterField,
    dataProvider: () => useOrganizationIdFilter(),
  },
  QboSyncStatus: {
    component: MultiselectJobFilterField,
    dataProvider: () => useBatchSyncStatusFilterProvider(),
  },
  QboSyncedBy: {
    component: MultiselectJobFilterField,
    dataProvider: () => useUsersWithUuidFromOrgFilter(),
  },
  Location: {
    component: LocationJobFilter,
  },
  User: {
    component: MultiselectJobFilterField,
    dataProvider: () => useAllUsersFilter(),
  },
  Business_unit: {
    component: MultiselectJobFilterField,
    dataProvider: () => useBusinessUnitFilter(),
  },
  Company: {
    component: MultiselectJobFilterField,
    dataProvider: () => useCompanyFilter(),
  },
}

const increasedWidthFilters = new Set<JobFilterLookupOptionType | BatchFilterLookupOptionType>([
  'ProjectId',
  'LocationId',
  'PickupLocationId',
  'DestinationLocationId',
])

const props = defineProps({
  filterType: {
    type: String as PropType<JobFilterType>,
    required: true,
  },
  subject: {
    type: String as PropType<JobFilterLookupOptionType | BatchFilterLookupOptionType>,
    required: true,
  },
  value: {
    type: Array as PropType<JobLookupFilterValue | null>,
    default: null,
  },
  comparison: {
    type: String as PropType<JobFilterComparison>,
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  definedOptions: {
    type: Array as PropType<MultiselectOption[]>,
    default: () => [],
  },
  supportedComparisons: {
    type: Array as PropType<JobFilterComparison[]>,
    default: () => [],
  },
  canBeEmpty: {
    type: Boolean,
    required: true,
  },
  entityType: {
    type: String as PropType<FiltersTopBarEntity>,
    default: undefined,
  },
})

const filterContextBridge = inject(filterContextBridgeKey, null)
const resolvedEntityType = props.entityType ?? filterContextBridge?.entityType

if (!resolvedEntityType) {
  throw new Error(
    '[useFilterContextBridge] No FilterContextBridge found in the component tree. ' +
      'Make sure a parent component calls useFilterContextProvider().',
  )
}

const registry =
  resolvedEntityType === 'Job' ? LookupComponentRegistry : BatchLookupComponentRegistry

const emit = defineEmits<{
  (e: 'input', value: JobLookupFilterValue | null): void
  (e: 'apply'): void
  (e: 'operation-change', value: JobFilterComparison): void
}>()

const resolvedProvider = shallowRef<Provider | undefined>(undefined)

watch(
  () => [props.filterType] as const,
  () => {
    resolvedProvider.value = undefined
    if (props.filterType === 'StringLookup' || props.filterType === 'IntLookup') {
      const factory = registry[props.subject]?.dataProvider
      if (factory) {
        resolvedProvider.value = factory()
      }
    }
  },
  { immediate: true },
)

const component = computed<Component>(() => {
  if (props.definedOptions && props.definedOptions.length > 0) {
    return MultiselectJobFilterField
  }
  if (props.filterType === 'StringLookup' || props.filterType === 'IntLookup') {
    return registry[props.subject].component
  }
  return BasicComponentRegistry[props.filterType]
})

const comparisonOptions = computed(() => {
  return SUPPORTED_JOB_FILTER_OPERATIONS.filter(
    (operation) =>
      operation.filterType === props.filterType &&
      props.supportedComparisons.includes(operation.comparison),
  ).map((operation) => ({
    label: operation.label,
    value: operation.comparison,
  }))
})

const componentDataProvider = computed(() => resolvedProvider.value)
const componentOptions = computed(() => {
  if (props.filterType === 'StringLookup' || props.filterType === 'IntLookup') {
    return LookupComponentRegistry[props.subject]?.componentOptions ?? {}
  }

  return {}
})

const options = computed(() => {
  const emptyOption = {
    label: 'Is empty',
    value: '',
  }
  const emptyOptions = props.canBeEmpty ? [emptyOption] : []

  if (props.definedOptions && props.definedOptions.length > 0) {
    return [...emptyOptions, ...props.definedOptions]
  }

  const provider = componentDataProvider.value
  if (provider) {
    if ('options' in provider) {
      return [...emptyOptions, ...(provider.options.value ?? [])]
    }
  }
  return undefined
})

function updateValue(value: JobLookupFilterValue | null) {
  emit('input', value)
}

function handleApply() {
  emit('apply')
}

const { filterFieldRef, isMultiselect, allFilteredSelected, handleToggleAll } = useSelectAllToggle(
  toRef(props, 'value'),
)

const cardCssClass = computed(() => {
  if (props.filterType === 'DateTime') {
    return 'date-filter-card'
  }
  if (increasedWidthFilters.has(props.subject)) {
    return 'filter-card-project'
  }
  return 'filter-card'
})

const cardContentCssClass = computed(() => {
  if (props.filterType === 'DateTime') {
    return 'date-filter-card-content'
  }
  return 'filter-card-content'
})

function onEnterPress() {
  if (
    props.filterType === 'Decimal' ||
    props.filterType === 'Double' ||
    props.filterType === 'Integer' ||
    props.filterType === 'Text'
  ) {
    handleApply()
  }
}
</script>

<template>
  <v-card
    class="filter-dropdown"
    :class="cardCssClass"
    @click.native.stop
    @keydown.enter="onEnterPress"
  >
    <div :class="cardContentCssClass">
      <component
        :is="component"
        ref="filterFieldRef"
        v-bind="componentOptions"
        :label="props.label"
        :value="props.value"
        :options="options"
        :comparison="props.comparison"
        :comparison-options="comparisonOptions"
        :data-provider="componentDataProvider"
        :can-be-empty="props.canBeEmpty"
        @input="updateValue"
        @operation-change="emit('operation-change', $event)"
        @apply="handleApply"
      />
    </div>

    <div class="filter-card-footer">
      <vs-button
        v-if="isMultiselect"
        class="toggle-all-btn"
        :label="allFilteredSelected ? 'Deselect All' : 'Select All'"
        small
        type="tertiary"
        @click="handleToggleAll"
      />
      <vs-button label="Apply" small type="primary" @click="handleApply" />
    </div>
  </v-card>
</template>

<style scoped>
.filter-dropdown {
  display: flex;
  flex-direction: column;
}

.filter-card,
.date-filter-card {
  color: var(--color-white) !important;
  display: flex;
  flex-direction: column;
}

.filter-card {
  width: 360px;
  max-height: 80vh;
}

.filter-card-project {
  width: 540px;
  max-height: 80vh;
}

.date-filter-card-content,
.filter-card-content {
  padding: 16px;
  overflow-y: auto;
  overflow-x: auto;
  max-width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
}

.date-filter-card-content {
  border-top: 1px solid var(--color-grey--lightest);
  border-right: 1px solid var(--color-grey--lightest);
  border-left: 1px solid var(--color-grey--lightest);
}

.filter-card-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid var(--color-grey--lightest);
  background-color: var(--color-white);
}

.toggle-all-btn {
  margin-right: auto;
}

.operation-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.operation-select {
  flex: 1;
  max-width: 200px;
}

.operation-select :deep(.v-input__slot) {
  border-radius: 6px;
  min-height: 40px;
}

@media (max-width: 600px) {
  .filter-card {
    width: 300px;
  }
}
</style>
